import Link from "next/link"
import { useRouter } from "next/router"
import React, { HTMLAttributes, memo } from "react"
import { UrlObject } from "url"
import { keepOnlyUtmParams } from "./utils/utmParams"

type NextLinkProps = HTMLAttributes<HTMLAnchorElement> & {
  href: string
  passHref?: boolean
  openInNewTab?: boolean
  children: React.ReactNode
  noScrollToTop?: boolean
  noEncoding?: boolean
}

const NextLink: React.FunctionComponent<NextLinkProps> = ({
  href,
  passHref,
  children,
  openInNewTab = false,
  noScrollToTop = false,
  noEncoding = false,
  ...props
}) => {
  const { asPath } = useRouter()

  // Extract the search string from the current URL
  const idx = asPath.indexOf("?")
  const search = idx >= 0 ? asPath.slice(idx).split("#")[0] : undefined
  // Extract the UTM parameters from the search string
  const utmSearch = keepOnlyUtmParams(search)

  // Extract the hash from the href
  const [hrefWithoutHash, onlyHash] = href ? href.split("#") : [""]

  // Extract the pathname and search string from the link
  const [hrefWithoutSearch, onlySearch] = hrefWithoutHash.split("?")

  // Combine the search string from the link and the UTM parameters
  const combinedSearchParams = utmSearch
    ? onlySearch
      ? `${onlySearch}&${utmSearch}`
      : utmSearch
    : onlySearch

  // Create the URL object for the link
  const link: UrlObject = {
    pathname: hrefWithoutSearch,
    search: combinedSearchParams,
    hash: onlyHash ?? null,
  }

  return (
    <Link
      href={noEncoding ? href : link}
      passHref
      scroll={!noScrollToTop}
      prefetch={false}
    >
      {openInNewTab ? (
        <a target="_blank" rel="noreferrer" {...props}>
          {children}
        </a>
      ) : passHref ? (
        children
      ) : (
        <a {...props}>{children}</a>
      )}
    </Link>
  )
}

export default memo(NextLink)
